/* What a customer has missed, worked out on the phone.
 *
 * The artboard's Updates sheet lists messages Damga sent. DAMGA SENDS NOTHING:
 * there is no channel, no push and no scheduled mail, and the sheet's own line
 * in the artboard ("never more than once a week per shop") would be a promise
 * about a thing that does not exist.
 *
 * So the sheet ships (empty is not wrong, ruled 2026-09-17) and its contents are
 * derived here from the cards the app already holds: a reward ready, a reward
 * about to lapse, a card two stamps away, a shop leaving. Every one of those is
 * a fact the wallet is already showing somewhere else; this is the one place
 * that gathers them. Nothing here reaches anybody who does not open the app,
 * and the sheet says so.
 *
 * READ STATE IS PER DEVICE, like the favourites, because there is nothing to
 * sync it with and it means nothing to anybody else.
 */
import { t } from "@damga/kit";

import type { Card } from "./App";

export type Notice = { id: string; text: string; kind: "ready" | "useBy" | "close" | "closing" };

/** A reward lapsing further out than this is not news yet. */
const LAPSE_SOON_DAYS = 14;
/** "Close" is the same two stamps the artboard's own notice used. */
const CLOSE_STAMPS = 2;

const KEY = "damga.notices.seen.v1";

export function readSeen(): string[] {
  try {
    const raw = localStorage.getItem(KEY);
    const parsed = raw ? (JSON.parse(raw) as unknown) : [];
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === "string") : [];
  } catch {
    return [];
  }
}

export function writeSeen(ids: string[]): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(ids));
  } catch {
    /* One device forgets what was read. Nothing else changes. */
  }
}

function days(iso: string): number {
  return Math.round((new Date(iso).getTime() - Date.now()) / 86_400_000);
}

export function noticesFor(cards: Card[]): Notice[] {
  const out: Notice[] = [];
  for (const c of cards.filter((x) => !x.archived_at)) {
    const shop = c.business_name;
    if (c.claimable > 0) {
      out.push({ id: `${c.membership_id}:ready:${c.claimable}`, kind: "ready", text: t("notice.ready", "en", { reward: c.reward_description, shop }) });
      if (c.reward_expires_at && days(c.reward_expires_at) <= LAPSE_SOON_DAYS) {
        out.push({
          id: `${c.membership_id}:useBy:${c.reward_expires_at}`,
          kind: "useBy",
          text: t("notice.useBy", "en", { shop, date: new Date(c.reward_expires_at).toLocaleDateString("en-GB", { day: "numeric", month: "long" }) }),
        });
      }
    } else {
      const togo = c.threshold - c.balance;
      if (togo > 0 && togo <= CLOSE_STAMPS) {
        out.push({ id: `${c.membership_id}:close:${c.balance}`, kind: "close", text: t("notice.close", "en", { n: togo, shop, reward: c.reward_description }) });
      }
    }
    if (c.status === "closing") {
      out.push({ id: `${c.membership_id}:closing`, kind: "closing", text: t("notice.closing", "en", { shop }) });
    }
  }
  const order = { ready: 0, useBy: 1, close: 2, closing: 3 };
  return out.sort((a, b) => order[a.kind] - order[b.kind]);
}
